import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ADMIN_API_URL } from "../../constants";
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const LeadDetailsPage = ({ addNotification, overrideLeadId, showBackButton = false, onBack }) => {
    const { leadId: paramLeadId } = useParams();
    const leadId = overrideLeadId || paramLeadId;
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check if coming from Quick Search mode
    const isFromSearchMode = new URLSearchParams(location.search).get('mode') === 'search';
    
    // Handle back navigation with proper mode preservation
    const handleBackNavigation = () => {
        if (showBackButton && onBack) {
            onBack();
        } else if (isFromSearchMode) {
            // Navigate back to leads page with search mode preserved
            navigate('/leads?mode=search');
        } else {
            // Normal navigation back to leads
            navigate('/leads');
        }
    };

    const [leadDetails, setLeadDetails] = useState(null);
    const [kycDocuments, setKycDocuments] = useState([]);
    const [loadingLead, setLoadingLead] = useState(true);
    const [leadError, setLeadError] = useState(null);
    const [loadingKyc, setLoadingKyc] = useState(false);
    const [kycError, setKycError] = useState(null);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    // Add states for document viewer
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);

    const [checkingCasheDuplicate, setCheckingCasheDuplicate] = useState(false);
    const [isEditingLoanRequest, setIsEditingLoanRequest] = useState(false);
    const [isSavingLoanRequest, setIsSavingLoanRequest] = useState(false);

    // Add state to track document type
    const [isPdfDocument, setIsPdfDocument] = useState(false);

    // Add states for editing
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    // Add states for document upload
    const [uploadingDocument, setUploadingDocument] = useState(null);
    const [documentUploads, setDocumentUploads] = useState({
        PAN: { file: null, number: '' },
        Aadhar: { file: null, number: '' },
        'Bank Statement': { file: null },
        'ITR Statement': { file: null }
    });

    // Document viewer controls
    const handleZoomIn = () => setZoomLevel(prev => prev + 0.1);
    const handleZoomOut = () => setZoomLevel(prev => Math.max(0.1, prev - 0.1));
    const handleResetZoom = () => setZoomLevel(1);
    const handleBackToDetails = () => setSelectedDocument(null);

    // Handle edit field change
    const handleFieldChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Check CASHe duplicate using external API and update UI
    const handleCheckCasheDuplicate = async () => {
        try {
            if (!leadDetails) return;
            const mobile = leadDetails?.mobile || '';
            const email = leadDetails?.email || '';

            if (!mobile && !email) {
                addNotification('warning', 'No mobile or email available to check CASHe duplicate');
                return;
            }

            setCheckingCasheDuplicate(true);

            const base = (process.env.REACT_APP_CREDIT_SERVICE_API_BASE_URL || '').replace(/\/$/, '');
            const endpoint = `${base}/cashe/check-duplicate`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ mobile_no: mobile, email_id: email, leadId: leadId })
            });

            if (!response.ok) {
                const err = await response.text();
                throw new Error(err || 'Failed to check CASHe duplicate');
            }

            const result = await response.json();

            // Update leadDetails.lenderInfo.casheDuplicateCheck with returned payload
            setLeadDetails(prev => ({
                ...prev,
                lenderInfo: {
                    ...prev?.lenderInfo,
                    casheDuplicateCheck: result
                }
            }));

            addNotification('success', `CASHe duplicate check: ${result.isDuplicate ? 'Duplicate' : 'Not duplicate'}`);
        } catch (error) {
            console.error('Error checking CASHe duplicate:', error);
            addNotification('error', `CASHe duplicate check failed: ${error.message}`);
        } finally {
            setCheckingCasheDuplicate(false);
        }
    };

    const lenderOptions = [
        { value: '', label: 'None' },
        { value: 'CASHE_001', label: 'CASHe' },
        { value: 'FIMONEY_001', label: 'FIMONEY' },
        { value: 'TRUSTPAISA_001', label: 'TrustPaisa' },
        { value: 'DWINGER_001', label: 'DWINGER' }
    ];

    const handleLenderChange = (value) => {
        setEditedData(prev => ({
            ...prev,
            lenderInfo: {
                ...prev?.lenderInfo,
                lenderId: value
            }
        }));
    };

    const handleCasheDuplicateFieldChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            lenderInfo: {
                ...prev?.lenderInfo,
                casheDuplicateCheck: {
                    ...(prev?.lenderInfo?.casheDuplicateCheck || {}),
                    [field]: value
                }
            }
        }));
    };

    const handleLoanRequestEditToggle = () => {
        if (!isEditingLoanRequest) {
            // initialize lenderInfo in editedData when entering loan request edit
            setEditedData(prev => ({
                ...prev,
                lenderInfo: leadDetails?.lenderInfo || {}
            }));
        }
        setIsEditingLoanRequest(!isEditingLoanRequest);
    };

    const saveLoanRequest = async () => {
        try {
            setIsSavingLoanRequest(true);
            const token = sessionStorage.getItem('authToken');
            if (!token) throw new Error('Authorization token not found. Please login again.');

            const body = { lenderInfo: editedData?.lenderInfo || {} };
            const response = await fetch(`${ADMIN_API_URL}update-lead-data/${leadId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update loan request');
            }

            // update local leadDetails
            setLeadDetails(prev => ({
                ...prev,
                lenderInfo: body.lenderInfo
            }));

            setIsEditingLoanRequest(false);
            addNotification('success', 'Loan request updated successfully');
        } catch (err) {
            console.error('Error saving loan request:', err);
            addNotification('error', `Failed to save loan request: ${err.message}`);
        } finally {
            setIsSavingLoanRequest(false);
        }
    };

    const cancelLoanRequestEdit = () => {
        // revert edited lenderInfo
        setEditedData(prev => ({
            ...prev,
            lenderInfo: leadDetails?.lenderInfo || {}
        }));
        setIsEditingLoanRequest(false);
    };

    // Handle save changes
    const handleSaveChanges = async () => {
        try {
            setIsSaving(true);
            const token = sessionStorage.getItem("authToken");
            if (!token) {
                throw new Error('Authorization token not found. Please login again.');
            }

            const response = await fetch(`${ADMIN_API_URL}update-lead-data/${leadId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body: JSON.stringify(editedData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update lead data');
            }

            const updatedData = await response.json();
            setLeadDetails(prev => ({
                ...prev,
                ...editedData
            }));
            setIsEditing(false);
            setEditedData({});
            addNotification('success', 'Lead details updated successfully');
        } catch (error) {
            console.error('Error updating lead:', error);
            addNotification('error', 'Failed to update lead details');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle edit mode toggle
    const handleEditToggle = () => {
        if (!isEditing) {
            // When entering edit mode, initialize with current values
            setEditedData({
                name: leadDetails?.applicantDetails?.name || leadDetails?.customerName || leadDetails?.name || '',
                email: leadDetails?.applicantDetails?.email || leadDetails?.email || '',
                phone: leadDetails?.applicantDetails?.phone || leadDetails?.mobile || '',
                productType: leadDetails?.productType || '',
                loanAmount: leadDetails?.loanAmount || '',
                customerId: leadDetails?.customerId || '',
                applicationType: leadDetails?.applicationType || '',
                panNumber: panDocument ? panDocument.documentValue : leadDetails?.panNumber || '',
                aadharNumber: aadharDocument ? aadharDocument.documentValue : leadDetails?.aadharNumber || '',
                lenderInfo: leadDetails?.lenderInfo || {}
            });
        }
        setIsEditing(!isEditing);
    };

    // Initialize editedData when leadDetails changes
    useEffect(() => {
        if (leadDetails) {
            setEditedData({
                name: leadDetails?.applicantDetails?.name || leadDetails?.customerName || leadDetails?.name || '',
                email: leadDetails?.applicantDetails?.email || leadDetails?.email || '',
                phone: leadDetails?.applicantDetails?.phone || leadDetails?.mobile || '',
                productType: leadDetails?.productType || '',
                loanAmount: leadDetails?.loanAmount || '',
                customerId: leadDetails?.customerId || '',
                applicationType: leadDetails?.applicationType || '',
                panNumber: leadDetails?.panNumber || '',
                aadharNumber: leadDetails?.aadharNumber || '',
                lenderInfo: leadDetails?.lenderInfo || {}
            });
        }
    }, [leadDetails]);

    useEffect(() => {
        if (leadId) {
            fetchLeadDetails();
            fetchUsers();
        }
    }, [leadId]);

    useEffect(() => {
        if (leadDetails && leadDetails.customerId) {
            fetchKycDocuments(leadDetails.customerId);
        } else {
            setKycDocuments([]);
        }
    }, [leadDetails]);

    // Fetch Lead Details
    const fetchLeadDetails = async () => {
        setLoadingLead(true);
        setLeadError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setLeadError("Authorization token not found.");
            setLoadingLead(false);
            addNotification("Authorization token not found. Please log in again.", 'error');
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}fetch-lead-data`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
                body: JSON.stringify({
                    minLimit: 0,
                    maxLimit: 1,
                    leadId: leadId // Filter by leadId
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch lead details.");
            }

            const result = await response.json();
            if (result.data) {
                // Handle both array and single object responses
                const leadData = Array.isArray(result.data) ? result.data[0] : result.data;
                if (leadData) {
                    setLeadDetails(leadData);
                } else {
                    setLeadDetails(null);
                    setLeadError("Lead not found.");
                }
            } else {
                setLeadDetails(null);
                setLeadError("Lead not found.");
            }
        } catch (err) {
            setLeadError(err.message);
            addNotification(`Error fetching lead details: ${err.message}`, 'error');
        } finally {
            setLoadingLead(false);
        }
    };

    // Fetch Users
    const fetchUsers = async () => {
        setLoadingUsers(true);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setLoadingUsers(false);
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}users`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch users.");
            }

            const result = await response.json();
            setUsers(result.data || []);
        } catch (err) {
            console.error("Error fetching users:", err);
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    // Fetch KYC documents
    const fetchKycDocuments = async (customerId) => {
        setLoadingKyc(true);
        setKycError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setKycError("Authorization token not found.");
            setLoadingKyc(false);
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}kyc/${customerId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch KYC documents.");
            }

            const result = await response.json();
            setKycDocuments(result.data || []);
        } catch (err) {
            setKycError(err.message);
            setKycDocuments([]);
        } finally {
            setLoadingKyc(false);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Approved': return 'badge-success';
            case 'Rejected': return 'badge-danger';
            case 'Disbursed': return 'badge-primary';
            case 'Under Review': return 'badge-info';
            case 'Application Submitted':
            case 'Documents Uploaded': return 'badge-warning';
            default: return 'badge-default';
        }
    };

    const panDocument = kycDocuments.find(doc => doc.documentType === 'PAN');
    const aadharDocument = kycDocuments.find(doc => doc.documentType === 'Aadhar');

    // Helper function to get user name from assignedTo ID
    const getAssignedUserName = (assignedToId) => {
        if (!assignedToId || !users.length) return 'N/A';
        const user = users.find(u => u.id === assignedToId);
        return user ? user.name : 'N/A';
    };

    // Validate document numbers
    const validateDocumentNumber = (type, number) => {
        if (type === 'PAN') {
            return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(number);
        } else if (type === 'Aadhar') {
            return /^\d{12}$/.test(number);
        }
        return true;
    };

    // Handle document number change
    const handleDocumentNumberChange = (type, value) => {
        setDocumentUploads(prev => ({
            ...prev,
            [type]: { ...prev[type], number: value }
        }));
    };

    // Handle document selection
    const handleDocumentSelect = (documentOrPath) => {
        const path = typeof documentOrPath === 'string'
            ? documentOrPath
            : (documentOrPath && (documentOrPath.documentPath || documentOrPath.documentUrl || documentOrPath.url)) || '';

        if (!path) {
            addNotification('Document not available to view', 'warning');
            return;
        }

        setSelectedDocument(path);
        try {
            setIsPdfDocument(path.toLowerCase().endsWith('.pdf'));
        } catch (e) {
            setIsPdfDocument(false);
        }
        setZoomLevel(1);
    };

    // Handle document upload
    const handleDocumentUpload = async (event, documentType) => {
        try {
        const file = event.target.files[0];
        if (!file) return;

            setUploadingDocument(documentType);
            const token = sessionStorage.getItem("authToken");
            if (!token) {
                addNotification('Authorization token not found', 'error');
                setUploadingDocument(null);
                return;
            }

            // 1. First check existing KYC documents
            const kycResponse = await fetch(`${ADMIN_API_URL}kyc/${leadDetails.customerId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": token,
                }
            });

            if (!kycResponse.ok) {
                throw new Error("Failed to check existing KYC documents");
            }

            // 2. Get lead data to get loanRequestId
            const leadResponse = await fetch(`${ADMIN_API_URL}fetch-lead-data`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": token,
                },
                body: JSON.stringify({
                    minLimit: 0,
                    maxLimit: 1,
                    leadId: leadId
                }),
            });

            if (!leadResponse.ok) {
                throw new Error("Failed to fetch lead data");
            }

            const leadResult = await leadResponse.json();
            // Handle both array and single object responses
            const leadData = Array.isArray(leadResult.data) ? leadResult.data[0] : leadResult.data;
            const loanRequestId = leadData?.loanRequestId || leadId; // Use leadId as fallback

            // 3. Upload document with all required data
        const formData = new FormData();
        formData.append('document', file);
        formData.append('customerId', leadDetails.customerId);
        formData.append('documentType', documentType);
            formData.append('loanRequestId', loanRequestId);
        
            // Add document value for PAN/Aadhar
        if (documentType === 'PAN' || documentType === 'Aadhar') {
                const number = documentUploads[documentType]?.number || 
                              (documentType === 'PAN' ? leadDetails?.panNumber : leadDetails?.aadharNumber);
                
                if (!number) {
                    addNotification(`Please enter ${documentType} number before uploading`, 'warning');
                setUploadingDocument(null);
                return;
            }

                if (!validateDocumentNumber(documentType, number)) {
                    // Warn but continue upload instead of blocking completely
                    addNotification(`Warning: ${documentType} number format seems invalid, proceeding with upload`, 'warning');
                }
            formData.append('documentValue', number);
        }

            const uploadResponse = await fetch(`${ADMIN_API_URL}kyc`, {
                method: 'POST',
                headers: {
                    'Authorization': token
                },
                body: formData
            });

            if (!uploadResponse.ok) {
                const errorData = await uploadResponse.json();
                throw new Error(errorData.message || 'Failed to upload document');
            }

            // 4. Refresh data after successful upload
            await Promise.all([
                fetchKycDocuments(leadDetails.customerId),
                fetchLeadDetails()
            ]);

            addNotification('Document uploaded successfully', 'success');
            
            // Clear the uploaded document data
            setDocumentUploads(prev => ({
                ...prev,
                [documentType]: { file: null, number: '' }
            }));

        } catch (error) {
            console.error('Error handling document:', error);
            addNotification(`Failed to process document: ${error.message}`, 'error');
        } finally {
            setUploadingDocument(null);
        }
    };

    if (loadingLead) {
        return (
            <>
                <Header />
                <div id="wrapper" style={{ paddingTop: '70px' }}>
                    <div className="content">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="hpanel">
                                    <div className="panel-body">
                                        <h2 className="font-light m-b-xs">Loading Application Details...</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </>
        );
    }

    if (leadError) {
        return (
            <>
                <Header />
                <div id="wrapper" style={{ paddingTop: '70px' }}>
                    <div className="content">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="hpanel">
                                    <div className="panel-body">
                                        <h2 className="font-light m-b-xs text-danger">Error: {leadError}</h2>
                                        <button className="btn btn-default" onClick={handleBackNavigation}>
                                            {showBackButton ? 'Back to Search' : (isFromSearchMode ? 'Back to Search' : 'Back to Leads')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </>
        );
    }

    if (!leadDetails) {
        return (
            <>
                <Header />
                <div id="wrapper" style={{ paddingTop: '70px' }}>
                    <div className="content">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="hpanel">
                                    <div className="panel-body">
                                        <h2 className="font-light m-b-xs">No Application Details Found.</h2>
                                        <button className="btn btn-default" onClick={handleBackNavigation}>
                                            {showBackButton ? 'Back to Search' : (isFromSearchMode ? 'Back to Search' : 'Back to Leads')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div id="wrapper" style={{ paddingTop: '70px' }}>
                <div className="content">
                    {selectedDocument ? (
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="hpanel">
                                    <div className="panel-body">
                                        <h2 className="font-light m-b-xs">Document Viewer</h2>
                                    </div>
                                </div>
                                <div className="hpanel">
                                    <div className="panel-body">
                                        <div className="document-viewer-controls mb-3">
                                            <button className="btn btn-default mr-2" onClick={() => setSelectedDocument(null)}>Back to Details</button>
                                            <button className="btn btn-default mr-2" onClick={handleZoomIn}>Zoom In</button>
                                            <button className="btn btn-default mr-2" onClick={handleZoomOut}>Zoom Out</button>
                                            <button className="btn btn-default" onClick={handleResetZoom}>Reset Zoom</button>
                                        </div>
                                        <div style={{
                                            overflow: 'auto',
                                            maxHeight: 'calc(100vh - 250px)',
                                            border: '1px solid #e0e0e0',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            {isPdfDocument ? (
                                                <iframe
                                                    src={selectedDocument}
                                                    title="Document Viewer"
                                                    style={{
                                                        width: '100%',
                                                        height: 'calc(100vh - 270px)',
                                                        border: 'none'
                                                    }}
                                                />
                                            ) : (
                                                <img 
                                                    alt="Document"
                                                    src={selectedDocument}
                                                    style={{
                                                        transform: `scale(${zoomLevel})`,
                                                        transformOrigin: 'center center',
                                                        maxWidth: '100%',
                                                        height: 'auto'
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="row mb-3">
                                <div className="col-lg-12" style={{ paddingLeft: '30px' }}>
                                    <button 
                                        className="btn btn-light" 
                                        onClick={handleBackNavigation}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '10px 20px',
                                            fontSize: '15px',
                                            fontWeight: '500',
                                            color: '#47484a',
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                            transition: 'all 0.3s ease',
                                            marginTop: '10px',
                                            marginBottom: '5px',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            textDecoration: 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#f8f9fa';
                                            e.target.style.borderColor = '#d9d9d9';
                                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                            e.target.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = '#ffffff';
                                            e.target.style.borderColor = '#e0e0e0';
                                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                            e.target.style.transform = 'translateY(0)';
                                        }}
                                        onMouseDown={(e) => {
                                            e.target.style.transform = 'translateY(1px)';
                                            e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                                        }}
                                        onMouseUp={(e) => {
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        <i 
                                            className="fa fa-arrow-left"
                                            style={{
                                                fontSize: '16px',
                                                marginRight: '2px'
                                            }}
                                        ></i>
                                        <span>{showBackButton ? 'Back to Search' : (isFromSearchMode ? 'Back to Search' : 'Back to Leads')}</span>
                                    </button>
                                </div>
                            </div>
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                            <h2 className="font-light m-b-xs">Application Details - {leadDetails?.id || leadId || 'N/A'}</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                        <div className="panel-heading d-flex justify-content-between align-items-center">
                                            <div>
                                    <i className="fa fa-info-circle"></i> Applicant Details
                                            </div>
                                            {!isEditing && !isFromSearchMode ? (
                                                <button className="btn btn-primary btn-sm" onClick={handleEditToggle}>
                                                    <i className="fa fa-edit"></i> Edit
                                                </button>
                                            ) : (isEditing && !isFromSearchMode) ? (
                                                <div>
                                                    <button 
                                                        className="btn btn-success btn-sm mr-2" 
                                                        onClick={handleSaveChanges}
                                                        disabled={isSaving}
                                                    >
                                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                    <button 
                                                        className="btn btn-default btn-sm" 
                                                        onClick={handleEditToggle}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : null}
                                </div>
                                <div className="panel-body">
                                            {isEditing ? (
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="form-group">
                                                            <label>Name</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control" 
                                                                value={editedData.name} 
                                                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                                                placeholder="Enter Name"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Email</label>
                                                            <input 
                                                                type="email" 
                                                                className="form-control" 
                                                                value={editedData.email} 
                                                                onChange={(e) => handleFieldChange('email', e.target.value)}
                                                                placeholder="Enter Email"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Phone</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control" 
                                                                value={editedData.phone} 
                                                                onChange={(e) => handleFieldChange('phone', e.target.value)}
                                                                placeholder="Enter Phone"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Product Type</label>
                                                            <select 
                                                                className="form-control" 
                                                                value={editedData.productType} 
                                                                onChange={(e) => handleFieldChange('productType', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="LENDER PAGE">LENDER PAGE</option>
                                                                <option value="PAYMENT">PAYMENT</option>
                                                                <option value="INVOICE">INVOICE</option>
                                                                <option value="PERSONAL LOAN">PERSONAL LOAN</option>
                                                                <option value="TWO WHEELER LOAN">TWO WHEELER LOAN</option>
                                                                <option value="BUSINESS EXPANSION LOAN">BUSINESS EXPANSION LOAN</option>
                                                                <option value="BUSINESS LOAN">BUSINESS LOAN</option>
                                                                <option value="COMMERCIAL VEHICLE LOAN">COMMERCIAL VEHICLE LOAN</option>
                                                                <option value="BUSINESS">BUSINESS</option>
                                                            </select>
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Loan Amount</label>
                                                            <input 
                                                                type="number" 
                                                                className="form-control" 
                                                                value={editedData.loanAmount} 
                                                                onChange={(e) => handleFieldChange('loanAmount', e.target.value)}
                                                                placeholder="Enter Loan Amount"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="form-group">
                                                            <label>Customer ID</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control" 
                                                                value={editedData.customerId} 
                                                                disabled
                                                                placeholder="Enter Customer ID"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Applied Date</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control" 
                                                                value={leadDetails?.createdAt || ''} 
                                                                disabled
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>PAN Number</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control" 
                                                                value={editedData.panNumber} 
                                                                disabled
                                                                placeholder="Enter PAN Number"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Aadhar Number</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control" 
                                                                value={editedData.aadharNumber} 
                                                                disabled
                                                                placeholder="Enter Aadhar Number"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="info-item">
                                                            <label>Name:</label>
                                                            <span>{leadDetails?.name || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Email:</label>
                                                            <span>{leadDetails?.email || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Phone:</label>
                                                            <span>{leadDetails?.mobile || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Product Type:</label>
                                                            <span>{leadDetails?.productType || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Loan Amount:</label>
                                                            <span>{leadDetails?.loanAmount || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Source:</label>
                                                            <span>{leadDetails?.source || 'N/A'}</span>
                                                        </div>
                                                        {/* Assigned Lender moved to Loan Request section below documents */}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="info-item">
                                                            <label>Customer ID:</label>
                                                            <span>{leadDetails?.customerId || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Applied Date:</label>
                                                            <span>{leadDetails?.createdAt || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>PAN Number:</label>
                                                            <span>{panDocument ? panDocument.documentValue : leadDetails?.panNumber || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Aadhar Number:</label>
                                                            <span>{aadharDocument ? aadharDocument.documentValue : leadDetails?.aadharNumber || 'N/A'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Assigned To:</label>
                                                            <span>{getAssignedUserName(leadDetails?.assignedTo)}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <label>Lead Status:</label>
                                                            <span>{leadDetails?.leadStatus || 'N/A'}</span>
                                                        </div>
                                                        {/* CASHe Duplicate Check moved to Loan Request section below documents */}
                                                    </div>
                                                </div>
                                            )}
                                </div>
                                        <style jsx>{`
                                            .info-item {
                                                margin-bottom: 10px;
                                                padding: 8px 12px;
                                                background: #f9f9f9;
                                                border-radius: 4px;
                                            }
                                            .info-item label {
                                                font-weight: 600;
                                                color: #676a6c;
                                                margin-right: 10px;
                                                min-width: 120px;
                                                display: inline-block;
                                            }
                                            .info-item span {
                                                color: #333;
                                            }
                                            .form-group {
                                                margin-bottom: 10px;
                                            }
                                            .form-group label {
                                                font-weight: 600;
                                                color: #676a6c;
                                                margin-bottom: 5px;
                                                display: block;
                                            }
                                            .form-control {
                                                border-radius: 4px;
                                                border: 1px solid #e5e6e7;
                                                padding: 6px 12px;
                                                height: 34px;
                                            }
                                            .form-control:focus {
                                                border-color: #1ab394;
                                                box-shadow: none;
                                            }
                                            .form-control[disabled] {
                                                background-color: #f9f9f9;
                                            }
                                        `}</style>
                            </div>
                        </div>
                    </div>

                    <div className="row m-t-md">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-heading">
                                    <i className="fa fa-file"></i> Documents
                                </div>
                                <div className="panel-body">
                                    <h4 className="mb-4">Documents {isFromSearchMode ? '(View Only)' : '(Select from Computer)'}</h4>
                                    <div className="row">
                                        {[
                                            { type: 'PAN', label: 'PAN Card Document', format: 'ABCDE1234F' },
                                            { type: 'Aadhar', label: 'Aadhar Card Document', format: '12 digits' },
                                            { type: 'BankStatement', label: 'Bank Statement Document' },
                                            { type: 'ITR', label: 'ITR Statement Document' }
                                        ].map((doc) => {
                                            const existingDoc = kycDocuments.find(d => d.documentType === doc.type);
                                            const needsNumber = doc.type === 'PAN' || doc.type === 'Aadhar';
                                            return (
                                                <div key={doc.type} className="col-md-6 mb-4">
                                                    <div className="document-upload-section p-3 border rounded">
                                                        <h5 className="mb-3">{doc.label}</h5>
                                                        {(() => {
                                                            const path = existingDoc && (existingDoc.documentPath || existingDoc.documentUrl || existingDoc.url);
                                                            return path ? (
                                                            <div className="mb-3">
                                                            <button 
                                                                    className="btn btn-info btn-sm mr-2"
                                                                onClick={() => handleDocumentSelect(path)}
                                                            >
                                                                View Document
                                                            </button>
                                                            </div>
                                                            ) : null;
                                                        })()}
                                            {needsNumber && !isFromSearchMode && (
                                            <div className="form-group">
                                                    <input
                                                        type="text"
                                                    className="form-control mb-2"
                                                        placeholder={`Enter ${doc.type} Number (${doc.format})`}
                                                    value={documentUploads[doc.type]?.number || ''}
                                                        onChange={(e) => handleDocumentNumberChange(doc.type, e.target.value)}
                                                    />
                                                </div>
                                            )}
                                        {!isFromSearchMode && (
                                            <div className="upload-controls">
                                                        <input
                                                            type="file"
                                                            id={`upload-${doc.type}`}
                                                            onChange={(e) => handleDocumentUpload(e, doc.type)}
                                                            accept=".jpg,.jpeg,.png,.pdf"
                                                            style={{ display: 'none' }}
                                                        />
                                                        <label 
                                                            htmlFor={`upload-${doc.type}`}
                                                    className="btn btn-primary btn-sm"
                                                >
                                                    {uploadingDocument === doc.type ? 'Uploading...' : 'Choose file'}
                                                        </label>
                                                    </div>
                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Loan Request section placed under Documents */}
                    <div className="row m-t-md">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-heading d-flex justify-content-between align-items-center">
                                    <div>
                                        <i className="fa fa-file-text-o"></i> Loan Request
                                    </div>
                                    {!isFromSearchMode && (
                                        <div>
                                            {!isEditingLoanRequest ? (
                                                <button className="btn btn-primary btn-sm" onClick={handleLoanRequestEditToggle}>
                                                    <i className="fa fa-edit"></i> Edit
                                                </button>
                                            ) : (
                                                <div>
                                                    <button
                                                        className="btn btn-success btn-sm mr-2"
                                                        onClick={saveLoanRequest}
                                                        disabled={isSavingLoanRequest}
                                                    >
                                                        {isSavingLoanRequest ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                    <button className="btn btn-default btn-sm" onClick={cancelLoanRequestEdit}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="panel-body">
                                    {isEditingLoanRequest ? (
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Assigned Lender</label>
                                                    <select
                                                        className="form-control"
                                                        value={editedData?.lenderInfo?.lenderId || ''}
                                                        onChange={(e) => handleLenderChange(e.target.value)}
                                                    >
                                                        {lenderOptions.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Duplicate Check</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={
                                                            (editedData?.lenderInfo?.casheDuplicateCheck)
                                                            ? (editedData.lenderInfo.casheDuplicateCheck.isDuplicate
                                                                ? `Duplicate (${editedData.lenderInfo.casheDuplicateCheck.reason || 'No reason'})`
                                                                : 'Not duplicate')
                                                            : ''
                                                        }
                                                        disabled
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="info-item">
                                                    <label>Assigned Lender:</label>
                                                    <span>{(leadDetails?.lenderInfo?.lenderId && lenderOptions.find(o=>o.value===leadDetails.lenderInfo.lenderId)?.label) || 'N/A'}</span>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="info-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <label>Duplicate Check:</label>
                                                        {leadDetails?.lenderInfo?.casheDuplicateCheck ? (
                                                            <span>{leadDetails.lenderInfo.casheDuplicateCheck.isDuplicate
                                                                ? `Duplicate (${leadDetails.lenderInfo.casheDuplicateCheck.reason || 'No reason'})`
                                                                : 'Not duplicate'}</span>
                                                        ) : null}
                                                    </div>
                                                    {/* Show check button when no data or explicitly not duplicate, and not in search mode; place inside same row */}
                                                    {!isFromSearchMode && (!leadDetails?.lenderInfo?.casheDuplicateCheck || leadDetails?.lenderInfo?.casheDuplicateCheck?.isDuplicate === false) && (
                                                        <button
                                                            className="btn btn-primary btn-xs"
                                                            onClick={handleCheckCasheDuplicate}
                                                            disabled={checkingCasheDuplicate}
                                                        >
                                                            {checkingCasheDuplicate ? 'Checking...' : 'Check CASHe Duplicate'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                        </>
                    )}
                </div>
                <Footer />
            </div>
        </>
    );
};

export default LeadDetailsPage; 